import { Component, Input } from '@angular/core';
import { StatusVariant } from '../data-table/data-table.model';

interface SessionStatusStyle {
  variant: StatusVariant;
  icon: string;
  label: string;
}

/**
 * Soft status pill for a report or session block status
 * (New / Open / Saved / Draft / Submitted / Approved / Rejected / Cancelled).
 * Reuses the global .pill / .pill-{variant} classes in styles.css and the globally loaded
 * Line Awesome icon font. This is the single source of truth for the status->colour/icon mapping,
 * replacing the per-template corner "sleeve" ribbons.
 *
 * "Open", "Saved" and "Draft" are the same lifecycle state (tblStatus 1) under three names: the LEAP v2
 * session API reports it as Open, the legacy timeline as Saved, and the LEAP v2 ILP and progress
 * report flows as Draft. All three map to the same pill.
 */
@Component({
  selector: 'app-session-status-pill',
  templateUrl: './session-status-pill.component.html',
  styleUrls: ['./session-status-pill.component.css'],
  standalone: false
})
export class SessionStatusPillComponent {
  @Input() status: string;

  private static readonly STYLES: { [status: string]: SessionStatusStyle } = {
    // "All" is not a real session status — it's the filter option that clears any status filter.
    All: { variant: 'gray', icon: 'la-layer-group', label: 'All' },
    New: { variant: 'amber', icon: 'la-star', label: 'New' },
    Saved: { variant: 'blue', icon: 'la-bookmark', label: 'Saved' },
    Open: { variant: 'blue', icon: 'la-bookmark', label: 'Open' },
    Draft: { variant: 'blue', icon: 'la-bookmark', label: 'Draft' },
    Submitted: { variant: 'purple', icon: 'la-paper-plane', label: 'Submitted' },
    Approved: { variant: 'green', icon: 'la-check-circle', label: 'Approved' },
    Rejected: { variant: 'red', icon: 'la-times-circle', label: 'Rejected' },
    // Terminal. Not a LeapDb StatusId — the API derives it from Orbit's own session status, for a slot with no
    // settled report: a tutor cancellation (no register rows) or a coordinator's diary cancel. A session Orbit
    // cancels on Submit because every learner gave 1+ days' notice is NOT this — it has a submitted report, so
    // it stays Submitted and takes the normal Accept/Reject footer (D25 supersedes D16's display clause).
    Cancelled: { variant: 'gray', icon: 'la-ban', label: 'Cancelled' }
  };

  private static readonly FALLBACK: SessionStatusStyle = { variant: 'gray', icon: 'la-question-circle', label: '' };

  get style(): SessionStatusStyle {
    return SessionStatusPillComponent.STYLES[this.status] || {
      ...SessionStatusPillComponent.FALLBACK,
      label: this.status || ''
    };
  }
}
